import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import type { HealthSubscoreAverages, MetricKey, Scope } from "@busright/data-contract";

import { Drillable } from "@/components/Drillable";
import { MetricLabel } from "@/components/MetricTooltip";

import { BulletGauge } from "./BulletGauge";

// =================================================================================================
// FhSubscoreBreakdown — the WHY behind the composite: five subscore bullets, each carrying a
// MetricLabel tooltip (the formula from the registry) AND a Drillable wrapper so a reader can go
// straight from "which subscore is weak" to "which devices, at what scope" without leaving the
// page. This is the "insight" link in flag → insight → action: the composite flag (CRITICAL/WARN
// count) points here for WHY, and each subscore points at the device rows for WHERE.
// =================================================================================================

export interface FhSubscoreBreakdownSpec {
  readonly key: keyof HealthSubscoreAverages;
  readonly metricKey: MetricKey;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly threshold: number;
}

export interface FhSubscoreBreakdownProps {
  readonly specs: readonly FhSubscoreBreakdownSpec[];
  readonly averages: HealthSubscoreAverages;
  readonly scope: Scope;
}

export const FhSubscoreBreakdown = ({
  specs,
  averages,
  scope,
}: FhSubscoreBreakdownProps): ReactNode => (
  <div>
    {specs.map((s) => (
      <Drillable
        key={s.key}
        metricKey={s.metricKey}
        scope={scope}
        label={`Drill into devices behind the ${s.label} subscore`}
      >
        <div className="fh-subscore-row">
          <MetricLabel metricKey={s.metricKey} label={s.label} />
          <div className="fh-subscore-gauge">
            <BulletGauge label="" value={averages[s.key]} threshold={s.threshold} icon={s.icon} />
          </div>
        </div>
      </Drillable>
    ))}
  </div>
);
